import type { DemoScript } from "../types.js";

/**
 * Settings → Tasks › "3×3 Eisenhower matrix" scope. Walks all three modes with
 * BOTH kinds of entry on screen — three tasks plus one EVENT, spread across
 * days so every block is readable: flags (matrix off) → "only for tasks"
 * (tasks recolor along the diagonal while the event visibly keeps its flag) →
 * "tasks & events" (the event joins the matrix). Deadline-derived urgency is
 * intentionally left to its dedicated settings demo. Days view is anchored on
 * today so the cross-day fixture placement is always on-grid. Fixture flags
 * match their matrix diagonals, so the scope-switch migration keeps every
 * placement.
 */
export const settingMatrixScope: DemoScript = {
  id: "setting-matrix-scope",
  titleKey: "demos.title.matrixScope",
  view: { kind: "days+matrix", count: 4, focusRef: "report" },
  stage: { width: 1000, height: 620 },
  // Deliberately below 1: this demo narrates three mode switches — give each
  // caption and recolor a beat more air than the default pacing.
  speed: 0.8,
  settings: async (ctx) => {
    await ctx.stores.settings.setMatrixScope("off");
  },
  fixture: [
    // errands sits high in the LOW band (score ≈ .33) so the flags→matrix
    // switch visibly recolors it green → yellow-green; the flag stays "low".
    { ref: "errands", titleKey: "demos.fx.errands", dayOffset: 0, hour: 9, durationMin: 60, priority: "low", matrixPos: { urgency: 0.05, importance: 0.6 } },
    { ref: "report", titleKey: "demos.fx.quarterlyReport", dayOffset: 0, hour: 13, durationMin: 90, priority: "high", matrixPos: { urgency: 0.8, importance: 0.85 } },
    { ref: "slides", titleKey: "demos.fx.prepSlides", dayOffset: 1, hour: 11, durationMin: 60, priority: "medium", matrixPos: { urgency: 0.5, importance: 0.55 } },
    { ref: "lunch", titleKey: "demos.fx.lunch", dayOffset: 2, hour: 12, durationMin: 60, isEvent: true, priority: "low", matrixPos: { urgency: 0.15, importance: 0.2 } },
  ],
  steps: [
    // Mode 1 — matrix off: tasks AND events carry classic flags.
    { kind: "caption", key: "demos.cap.matrixScope.flags" },
    { kind: "moveTo", target: { kind: "task", ref: "errands" } },
    { kind: "moveTo", target: { kind: "task", ref: "slides" } },
    { kind: "moveTo", target: { kind: "task", ref: "report" } },
    { kind: "moveTo", target: { kind: "task", ref: "lunch" } },
    { kind: "pause", ms: 400 },
    // Mode 2 — "only for tasks": tasks recolor, the event doesn't.
    {
      kind: "action",
      run: (ctx) => ctx.stores.settings.setMatrixScope("tasks"),
      captionKey: "demos.cap.matrixScope.matrixOn",
    },
    { kind: "pause", ms: 900 },
    { kind: "moveTo", target: { kind: "matrixCell", col: 2, row: 2 } },
    { kind: "press" },
    { kind: "release" },
    {
      kind: "action",
      run: (ctx) =>
        ctx.stores.tasks.edit(ctx.id("report"), {
          matrix_pos: { urgency: 0.92, importance: 0.9, urgencyAuto: false },
        }),
      captionKey: "demos.cap.matrixScope.diagonal",
    },
    { kind: "moveTo", target: { kind: "task", ref: "report" } },
    { kind: "pause", ms: 400 },
    { kind: "moveTo", target: { kind: "task", ref: "lunch" }, captionKey: "demos.cap.matrixScope.eventsKeepFlags" },
    { kind: "pause", ms: 1100 },
    // Mode 3 — "tasks & events": the event joins the matrix…
    {
      kind: "action",
      run: (ctx) => ctx.stores.settings.setMatrixScope("tasks_events"),
      captionKey: "demos.cap.matrixScope.eventsToo",
    },
    { kind: "pause", ms: 900 },
    // …and gets its own placement: green (low flag) → orange (urgent, not
    // important) recolors the block right under the pointer.
    {
      kind: "action",
      run: (ctx) =>
        ctx.stores.tasks.edit(ctx.id("lunch"), {
          matrix_pos: { urgency: 0.95, importance: 0.35, urgencyAuto: false },
        }),
      captionKey: "demos.cap.matrixScope.eventPlaced",
    },
    { kind: "pause", ms: 1100 },
  ],
};
